import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaExpandAlt, FaCalendar, FaUsers, FaClock } from 'react-icons/fa';

function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const projectsData = [
    {
      id: 1,
      title: "Modern Kitchen Renovation",
      category: "residential",
      shortDescription: "Complete kitchen transformation with high-end finishes",
      fullDescription: "A complete kitchen renovation featuring custom cabinets, quartz countertops, and state-of-the-art appliances. This project transformed a dated kitchen into a modern cooking and entertaining space.",
      image: "https://source.unsplash.com/800x600/?kitchen,modern",
      details: {
        duration: "6 weeks",
        team: "4 specialists",
        completion: "2023"
      }
    },
    {
      id: 2,
      title: "Luxury Bathroom Remodel",
      category: "residential",
      shortDescription: "Spa-inspired master bathroom renovation",
      fullDescription: "A luxurious master bathroom renovation with heated floors, custom shower, freestanding tub, and premium fixtures. Created a personal spa experience.",
      image: "https://source.unsplash.com/800x600/?bathroom,luxury",
      details: {
        duration: "4 weeks",
        team: "3 specialists",
        completion: "2023"
      }
    },
    {
      id: 3,
      title: "Commercial Office Space",
      category: "commercial",
      shortDescription: "Modern office renovation for tech startup",
      fullDescription: "Complete office space renovation including open-concept workspace, meeting rooms, and collaborative areas. Modern design with focus on functionality.",
      image: "https://source.unsplash.com/800x600/?office,modern",
      details: {
        duration: "12 weeks",
        team: "8 specialists",
        completion: "2023"
      }
    },
    {
      id: 4,
      title: "Outdoor Living Space",
      category: "outdoor",
      shortDescription: "Custom deck and landscaping project",
      fullDescription: "Created an expansive outdoor living area with custom deck, built-in seating, outdoor kitchen, and landscaping. Perfect for entertaining.",
      image: "https://source.unsplash.com/800x600/?deck,outdoor",
      details: {
        duration: "8 weeks",
        team: "5 specialists",
        completion: "2023"
      }
    },
    {
      id: 5,
      title: "Restaurant Renovation",
      category: "commercial",
      shortDescription: "Complete restaurant redesign and renovation",
      fullDescription: "Full-scale restaurant renovation including kitchen upgrade, dining area redesign, and new bar installation. Modern industrial aesthetic.",
      image: "https://source.unsplash.com/800x600/?restaurant,interior",
      details: {
        duration: "10 weeks",
        team: "6 specialists",
        completion: "2023"
      }
    },
    {
      id: 6,
      title: "Home Addition",
      category: "residential",
      shortDescription: "Two-story home addition with master suite",
      fullDescription: "Added a two-story addition including a master suite, home office, and expanded living area. Seamlessly integrated with existing architecture.",
      image: "https://source.unsplash.com/800x600/?house,modern",
      details: {
        duration: "16 weeks",
        team: "7 specialists",
        completion: "2023"
      }
    }
  ];

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'outdoor', label: 'Outdoor' }
  ];

  const filteredProjects = filter === 'all' 
    ? projectsData 
    : projectsData.filter(project => project.category === filter);

  return (
    <div className="projects-container">
      {/* Hero Section */}
      <motion.section 
        className="projects-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="projects-hero-content">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Our Projects
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Explore our portfolio of successful projects and transformations
          </motion.p>
        </div>
      </motion.section>

      {/* Filter Section */}
      <section className="filter-section">
        <div className="filter-buttons">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              className={`filter-button ${filter === category.id ? 'active' : ''}`}
              onClick={() => setFilter(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.label}
            </motion.button>
          ))}
        </div>
      </section>

      {/* Projects Grid */}
      <motion.section 
        className="projects-grid"
        layout
      >
        <AnimatePresence>
          {filteredProjects.map((project) => (
            <motion.div
              layout
              key={project.id}
              className="project-card"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ y: -10 }}
              onClick={() => setSelectedProject(project)}
            >
              <div className="project-image" style={{ backgroundImage: `url(${project.image})` }}>
                <div className="project-overlay">
                  <FaExpandAlt />
                </div>
              </div>
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.shortDescription}</p>
                <span className="category-tag">{project.category}</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.section>

      {/* Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal-content"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
            >
              <button 
                className="close-button"
                onClick={() => setSelectedProject(null)}
              >
                <FaTimes />
              </button>
              
              <div className="modal-image" style={{ backgroundImage: `url(${selectedProject.image})` }} />
              
              <div className="modal-details">
                <h2>{selectedProject.title}</h2>
                <p className="full-description">{selectedProject.fullDescription}</p>
                
                <div className="project-stats">
                  <div className="stat">
                    <FaClock />
                    <span>{selectedProject.details.duration}</span>
                  </div>
                  <div className="stat">
                    <FaUsers />
                    <span>{selectedProject.details.team}</span>
                  </div>
                  <div className="stat">
                    <FaCalendar />
                    <span>{selectedProject.details.completion}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Projects;